/**
 * UserManagementTab · v2.7.0 batch DO++++
 * =======================================
 * Single page hosting the 8 role-segmented user views requested in FEATURE 2.
 * The sub-tab is driven by the URL hash (`#sub=users-{role}`) so the sidebar
 * links can deep-link directly.
 *
 * Role map → which existing component handles the view:
 *   • users-riders            → CustomersTab (rider customers collection)
 *   • users-drivers           → DriversTab roleFilter="driver" (rank-and-file)
 *   • users-team-leaders      → DriversTab roleFilter="team_leader"
 *   • users-field-supervisors → DriversTab roleFilter="field_supervisor"
 *   • users-dispatchers       → DriversTab roleFilter="dispatcher"
 *   • users-general-partners  → DriversTab roleFilter="operating_partner"
 *   • users-developers        → DriversTab roleFilter="developer" (dev only)
 *   • users-admins            → DriversTab roleFilter="admin" (super only)
 *
 * The component receives `customersComponent` + `driversComponent` props from
 * AdminDashboardPage so we don't have to duplicate the JSX for those tabs.
 */
import { useHashSubTab } from "./AdminSidebar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { Users, ShieldCheck, Lock, User, Crown, Code2, Headphones, Car, Radar, Inbox, ClipboardList, MessageCircle } from "lucide-react";
import ApplicationsPanel from "./ApplicationsPanel";  // v2.7.65+E77
import ContractorSetupsPanel from "./ContractorSetupsPanel";  // v2.7.65+E135
import ConsentAuditPanel from "./ConsentAuditPanel";  // v2.7.65+E168 · SMS opt-in audit

// v2.7.0 batch DO+++++++ · Premium "bad-ass" role icon glyphs.
// Each entry pairs a lucide icon with a neon-accent class so the icon both
// pops in color AND scales on hover (see `.um-icon-tile` in index.css).
const ROLE_ICONS = {
  "users-riders":            { Icon: User,        glow: "text-violet-300",  bg: "bg-violet-500/12 border-violet-400/30" },
  "users-drivers":           { Icon: Car,         glow: "text-sky-300",     bg: "bg-sky-500/12 border-sky-400/30" },
  "users-team-leaders":      { Icon: ShieldCheck, glow: "text-amber-300",   bg: "bg-amber-500/12 border-amber-400/30" },
  "users-field-supervisors": { Icon: Radar,       glow: "text-indigo-300",  bg: "bg-indigo-500/12 border-indigo-400/30" },
  "users-dispatchers":       { Icon: Headphones,  glow: "text-yellow-300",    bg: "bg-yellow-500/12 border-yellow-400/30" },
  "users-general-partners":  { Icon: Crown,       glow: "text-fuchsia-300", bg: "bg-fuchsia-500/12 border-fuchsia-400/30" },
  "users-developers":        { Icon: Code2,       glow: "text-emerald-300", bg: "bg-emerald-500/12 border-emerald-400/30" },
  "users-admins":            { Icon: Lock,        glow: "text-rose-300",    bg: "bg-rose-500/12 border-rose-400/30" },
  "users-applications":      { Icon: Inbox,       glow: "text-amber-300",   bg: "bg-amber-500/12 border-amber-400/30" }, // v2.7.65+E77
  "users-contractor-setups": { Icon: ClipboardList, glow: "text-rose-300",   bg: "bg-rose-500/12 border-rose-400/30" },   // v2.7.65+E135
  "users-consent-audit":     { Icon: MessageCircle, glow: "text-emerald-300", bg: "bg-emerald-500/12 border-emerald-400/30" }, // v2.7.65+E168
};

const TAB_DEFS = [
  { value: "users-riders",            label: "Riders",            kind: "rider"    },
  { value: "users-drivers",           label: "Regular Drivers",   kind: "driver",            roleFilter: "driver" },
  { value: "users-team-leaders",      label: "Team Leaders",      kind: "driver",            roleFilter: "team_leader" },
  { value: "users-field-supervisors", label: "Field Supervisors", kind: "driver",            roleFilter: "field_supervisor" },
  { value: "users-dispatchers",       label: "Dispatchers",       kind: "driver",            roleFilter: "dispatcher" },
  { value: "users-general-partners",  label: "General Partners",  kind: "driver",            roleFilter: "operating_partner" },
  { value: "users-developers",        label: "Developer",         kind: "driver",            roleFilter: "developer",      superOnly: "developer" },
  { value: "users-admins",            label: "Admin",             kind: "driver",            roleFilter: "admin",          superOnly: true },
  { value: "users-applications",      label: "Applications",      kind: "applications" },  // v2.7.65+E77 · Public "Drive With Us" submissions
  { value: "users-contractor-setups", label: "Contractor Packets", kind: "contractor-setups" },  // v2.7.65+E135 · All contractor-setup packets incl. orphans
  { value: "users-consent-audit",     label: "Consent Audit",      kind: "consent-audit" },  // v2.7.65+E168 · SMS opt-in campaign gate
];

export default function UserManagementTab({ adminRole, adminRoles = [], CustomersComponent, DriversComponent }) {
  const [sub, setSub] = useHashSubTab("users-drivers");
  const isDeveloper = adminRoles.includes("developer");
  const isSuper = adminRoles.some((r) => ["admin", "operating_partner", "developer", "supervisor"].includes(r));

  const visible = TAB_DEFS.filter((t) => {
    if (t.superOnly === "developer") return isDeveloper;
    if (t.superOnly) return isSuper;
    return true;
  });

  return (
    <div className="space-y-3" data-testid="user-mgmt-root">
      <div>
        <h2 className="text-xl font-extrabold tracking-tight">User Management</h2>
        <p className="text-xs text-zinc-500 mt-1">
          Role-segmented account directory · queries the multi-role array
        </p>
      </div>
      <Tabs value={visible.some((v) => v.value === sub) ? sub : visible[0]?.value} onValueChange={setSub}>
        <TabsList className="flex flex-wrap gap-1.5 bg-zinc-900/60 backdrop-blur-sm border border-zinc-800 p-1.5 h-auto">
          {visible.map((t) => {
            const meta = ROLE_ICONS[t.value] || { Icon: Users, glow: "text-zinc-300", bg: "bg-zinc-700/30 border-zinc-700" };
            const Icon = meta.Icon;
            const active = sub === t.value;
            return (
              <TabsTrigger
                key={t.value}
                value={t.value}
                data-testid={`um-tab-${t.value}`}
                className={`text-xs gap-2 ${active ? "data-[state=active]:bg-zinc-950 data-[state=active]:border-amber-400/40" : ""} group`}
              >
                <span className={`inline-flex h-6 w-6 items-center justify-center rounded-md border ${meta.bg} ${meta.glow} um-icon-tile`}>
                  <Icon className="h-3.5 w-3.5"/>
                </span>
                <span className={active ? "font-bold" : ""}>{t.label}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>
        {visible.map((t) => (
          <TabsContent key={t.value} value={t.value}>
            {t.kind === "rider" ? (
              CustomersComponent ? <CustomersComponent/> : <div className="p-6 text-zinc-500 text-sm">Riders view not provided.</div>
            ) : t.kind === "applications" ? (
              <ApplicationsPanel/>
            ) : t.kind === "contractor-setups" ? (
              <ContractorSetupsPanel/>
            ) : t.kind === "consent-audit" ? (
              <ConsentAuditPanel/>
            ) : (
              DriversComponent ? <DriversComponent adminRole={adminRole} roleFilter={t.roleFilter}/> : <div className="p-6 text-zinc-500 text-sm">Drivers view not provided.</div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
